import type { ReactNode } from 'react';
import { Alert, Button, Modal } from 'antd';
import { PreSalesQuestion } from '../../../dto/model';
import { strings } from '../../../lang';
import { PreSalesAnswers } from '../../../utils/pre-sales-questionnaire-evaluator';
import { PreSalesQuestionnaireCard } from './pre-sales-questionnaire-card';
import { PreSalesQuestionField } from './pre-sales-question-field';

type PreSalesQuestionnaireFormProps = {
  unifiedFlow?: boolean;
  answers: PreSalesAnswers;
  touchedQuestions: Record<string, boolean>;
  visibleQuestions: PreSalesQuestion[];
  cancellationQuestions: PreSalesQuestion[];
  cancellationMessages: string[];
  isCancellationModalOpen: boolean;
  cancellationEvaluationFailed: boolean;
  isFormLocked: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  isEvaluatingCancellation: boolean;
  isSubmitDisabled: boolean;
  canSave: boolean;
  footerActions?: ReactNode;
  onAnswerChange: (
    questionKey: string,
    value: string | number | string[] | undefined,
    shouldEvaluate?: boolean,
  ) => void;
  onTouched: (questionKey: string) => void;
  onAnswerBlur: (questionKey: string, shouldEvaluate?: boolean) => void;
  onSave: () => void;
  onSubmit: () => void;
  onDismissCancellationModal: () => void;
  onConfirmCancellation?: () => void;
  isCancellationConfirmDisabled?: boolean;
};

export const PreSalesQuestionnaireForm = ({
  unifiedFlow = false,
  answers,
  touchedQuestions,
  visibleQuestions,
  cancellationQuestions,
  cancellationMessages,
  isCancellationModalOpen,
  cancellationEvaluationFailed,
  isFormLocked,
  isSaving,
  isSubmitting,
  isEvaluatingCancellation,
  isSubmitDisabled,
  canSave,
  footerActions,
  onAnswerChange,
  onTouched,
  onAnswerBlur,
  onSave,
  onSubmit,
  onDismissCancellationModal,
  onConfirmCancellation,
  isCancellationConfirmDisabled,
}: PreSalesQuestionnaireFormProps) => (
  <>
    <PreSalesQuestionnaireCard
      footer={(
        <>
          {!unifiedFlow && (
            <Button onClick={onSave} loading={isSaving} disabled={!canSave || isFormLocked}>
              {strings.preSalesQuestionnaire.saveButton}
            </Button>
          )}
          <Button
            type="primary"
            data-presales-submit
            onClick={onSubmit}
            loading={isSubmitting || (!unifiedFlow && isEvaluatingCancellation)}
            disabled={isSubmitDisabled}
          >
            {strings.preSalesQuestionnaire.submitButton}
          </Button>
          {footerActions}
        </>
      )}
    >
      {cancellationEvaluationFailed && (
        <Alert
          type="error"
          showIcon
          message={strings.preSalesQuestionnaire.cancellationEvaluationError}
        />
      )}
      {visibleQuestions.map((question) => (
        <PreSalesQuestionField
          key={question.questionKey}
          question={question}
          answers={answers}
          isTouched={touchedQuestions[question.questionKey] === true}
          isLocked={isFormLocked}
          onAnswerChange={onAnswerChange}
          onTouched={onTouched}
          onAnswerBlur={onAnswerBlur}
          skipQualificationOnSubmit={unifiedFlow}
        />
      ))}
    </PreSalesQuestionnaireCard>

    <Modal
      title={strings.preSalesQuestionnaire.cancellationTitle}
      open={isCancellationModalOpen}
      onCancel={isSubmitting ? undefined : onDismissCancellationModal}
      closable={!isSubmitting}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      footer={[
        <Button key="close" disabled={isSubmitting} onClick={onDismissCancellationModal}>
          {strings.close}
        </Button>,
        <Button
          key="submit"
          type="primary"
          data-presales-submit
          onClick={onConfirmCancellation ?? onSubmit}
          loading={isSubmitting || (!unifiedFlow && isEvaluatingCancellation)}
          disabled={isCancellationConfirmDisabled ?? isSubmitDisabled}
        >
          {strings.preSalesQuestionnaire.submitButton}
        </Button>,
      ]}
    >
      {cancellationMessages.length > 0 && (
        <ul className="bp-presales-questionnaire__cancellation-list">
          {cancellationMessages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
      {cancellationQuestions.length > 0 && (
        <>
          <p>{strings.preSalesQuestionnaire.cancellationReasonQuestionsIntro}</p>
          <ul className="bp-presales-questionnaire__cancellation-list">
            {cancellationQuestions.map((question) => (
              <li key={question.questionKey}>{question.questionText || question.questionKey}</li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  </>
);
